import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { LectureRecord, LectureSnapshot } from '@wx-weiqi/contracts';
import { userErrorMessage } from '@wx-weiqi/contracts';
import type { LectureRecordApi } from '../../services/lecture-records';
import { LectureRecordStore } from '../../stores/lecture-record-store';
import { lecturePositionLabel, lectureSnapshotKey } from '../../domain/lecture-records';
import { LectureDiagram } from './LectureDiagram';
import { renderLecturePrint } from './lecture-print';
import './lecture-records.css';
export function LectureRecordsPanel({
  api,
  scope,
  active,
  capture,
  available,
  onClose,
}: {
  api: LectureRecordApi;
  scope: string;
  active: boolean;
  capture: () => LectureSnapshot;
  available: boolean;
  onClose: () => void;
}) {
  const [store] = useState(() => {
    let storage: Storage | null = null;
    try {
      storage = globalThis.localStorage;
    } catch {
      /* Browsers may deny local storage. */
    }
    return new LectureRecordStore(api, scope, storage);
  });
  useEffect(() => store.attach(), [store]);
  const state = useSyncExternalStore(store.subscribe, store.snapshot),
    draft = store.active();
  const [view, setView] = useState<'EDITOR' | 'LIST'>('EDITOR'),
    [diagram, setDiagram] = useState(false),
    [exported, setExported] = useState<LectureRecord[] | null>(null),
    [selected, setSelected] = useState<{ recordId: string; version: number }[]>([]),
    [exportBusy, setExportBusy] = useState(false),
    [error, setError] = useState(''),
    [pages, setPages] = useState(0);
  const textarea = useRef<HTMLTextAreaElement>(null),
    dialog = useRef<HTMLDialogElement>(null),
    frame = useRef<HTMLIFrameElement>(null);
  const captureRef = useRef(capture);
  captureRef.current = capture;
  const start = () => {
    try {
      store.start(captureRef.current());
      setView('EDITOR');
      setError('');
    } catch (e) {
      setError(userErrorMessage(e, '当前局面无法记录，请稍后重试。'));
    }
  };
  useEffect(() => {
    if (active && !store.active() && available) {
      try {
        store.start(captureRef.current());
      } catch (e) {
        setError(userErrorMessage(e, '当前局面无法记录。'));
      }
    }
  }, [active, available, store]);
  useEffect(() => {
    if (active && view === 'EDITOR') textarea.current?.focus();
  }, [active, view]);
  useEffect(() => {
    const preventLoss = (e: BeforeUnloadEvent) => {
      if (
        store.snapshot().storageError &&
        store.snapshot().drafts.some((d) => d.revision !== d.savedRevision)
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', preventLoss);
    return () => window.removeEventListener('beforeunload', preventLoss);
  }, [store]);
  useEffect(() => {
    if (diagram || exported) {
      dialog.current?.showModal();
    } else dialog.current?.close();
  }, [diagram, exported]);
  useEffect(() => {
    if (!exported || !frame.current?.contentDocument) return;
    let stale = false;
    setPages(0);
    void renderLecturePrint(frame.current.contentDocument, exported)
      .then((n) => {
        if (!stale) setPages(n);
      })
      .catch((e) => {
        if (!stale) setError(userErrorMessage(e, '打印预览生成失败。'));
      });
    return () => {
      stale = true;
    };
  }, [exported]);
  const dirty = !!draft && (draft.revision !== draft.savedRevision || draft.operation !== null);
  let changed: boolean;
  try {
    changed = !!draft && lectureSnapshotKey(draft.snapshot) !== lectureSnapshotKey(capture());
  } catch {
    changed = !!draft;
  }
  const format = (kind: 'BOLD' | 'BULLET') => {
    const input = textarea.current;
    if (!input || !draft) return;
    const from = input.selectionStart,
      to = input.selectionEnd,
      part = draft.text.slice(from, to);
    const value =
      kind === 'BOLD'
        ? `**${part || '重点'}**`
        : part
            .split('\n')
            .map((line) => `- ${line}`)
            .join('\n');
    store.edit({ text: draft.text.slice(0, from) + value + draft.text.slice(to) });
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(from, from + value.length);
    });
  };
  const exportRecords = async () => {
    setError('');
    setExportBusy(true);
    try {
      setExported(await api.export(selected));
    } catch (e) {
      setError(userErrorMessage(e, '导出失败，请刷新记录列表后重试。'));
    } finally {
      setExportBusy(false);
    }
  };
  return (
    <section hidden={!active} className="lecture-record-card" aria-label="讲解记录">
      <header>
        <strong>讲解记录</strong>
        <nav aria-label="记录操作">
          <button
            type="button"
            disabled={state.busy}
            onClick={() => {
              setView(view === 'LIST' ? 'EDITOR' : 'LIST');
              if (view !== 'LIST') setSelected([]);
              if (view !== 'LIST') void store.loadMore(true);
            }}
          >
            {view === 'LIST' ? '返回编辑' : '记录列表'}
          </button>
          <button type="button" onClick={onClose}>
            课堂笔记
          </button>
        </nav>
      </header>
      {view === 'EDITOR' ? (
        <>
          {draft ? (
            <>
              <div className="lecture-record-source">
                <span title={draft.snapshot.source.name}>
                  {lecturePositionLabel(draft.snapshot)} · {draft.snapshot.source.name}
                </span>
                <button type="button" onClick={() => setDiagram(true)}>
                  查看棋图
                </button>
              </div>
              {changed ? (
                <p className="lecture-record-mismatch">棋盘已变化，正文仍对应上方记录棋图。</p>
              ) : null}
              <label className="lecture-record-title">
                <span className="sr-only">记录标题</span>
                <input
                  maxLength={120}
                  value={draft.title}
                  placeholder="标题（选填）"
                  onChange={(e) => store.edit({ title: e.target.value })}
                />
              </label>
              <div className="lecture-record-format" role="toolbar" aria-label="记录文字格式">
                <button type="button" onClick={() => format('BOLD')}>
                  B 加粗
                </button>
                <button type="button" onClick={() => format('BULLET')}>
                  • 要点
                </button>
                <span>**加粗** · - 要点</span>
              </div>
              <textarea
                ref={textarea}
                aria-label="分析正文"
                value={draft.text}
                maxLength={12_000}
                placeholder="记录这一局面的判断、关键变化与教学要点…"
                onChange={(e) => store.edit({ text: e.target.value })}
              />
              <footer>
                <span role="status">
                  {state.message || (dirty ? '未保存 · 本机草稿' : '已保存')}
                </span>
                <button type="button" disabled={!available || state.busy} onClick={start}>
                  新增局面
                </button>
                <button
                  className="lecture-record-save"
                  type="button"
                  disabled={state.busy || !dirty || !draft.text.trim()}
                  onClick={() => void store.save()}
                >
                  {state.busy ? '保存中…' : draft.operation ? '重试保存' : '保存记录'}
                </button>
              </footer>
            </>
          ) : (
            <div className="lecture-record-empty">
              <p>将当前局面和讲解一起保存。</p>
              <button type="button" disabled={!available} onClick={start}>
                记录当前局面
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="lecture-record-list">
          <div className="lecture-record-list-tools">
            <span>
              本机草稿 {state.drafts.length} · 已保存 {state.records.length}
              {state.nextCursor ? '+' : ''}
            </span>
            <button
              type="button"
              disabled={state.busy}
              onClick={() => {
                setSelected([]);
                void store.loadMore(true);
              }}
            >
              刷新
            </button>
          </div>
          {state.drafts.length > 0 ? (
            <details>
              <summary>本机草稿与编辑副本</summary>
              {state.drafts.map((d) => (
                <div className="lecture-record-row" key={d.id}>
                  <button
                    type="button"
                    onClick={() => {
                      store.select(d.id);
                      setView('EDITOR');
                    }}
                  >
                    {d.title || lecturePositionLabel(d.snapshot)}
                    {d.revision !== d.savedRevision ? ' · 未保存' : ''}
                  </button>
                  <button
                    type="button"
                    disabled={state.busy || d.operation !== null}
                    onClick={() => {
                      if (window.confirm('移除此设备上的草稿？服务器中已保存的记录会保留。'))
                        store.discard(d.id);
                    }}
                  >
                    移除草稿
                  </button>
                </div>
              ))}
            </details>
          ) : null}
          {selected.length > 0 ? (
            <div className="lecture-export-order">
              <strong>导出顺序（{selected.length}/50）</strong>
              {selected.map((r, i) => (
                <div className="lecture-record-row" key={r.recordId}>
                  <span>
                    {i + 1}.{' '}
                    {state.records.find((s) => s.recordId === r.recordId)?.title || '未命名记录'}
                  </span>
                  <button
                    type="button"
                    disabled={i === 0}
                    aria-label={`将第${i + 1}条前移`}
                    onClick={() =>
                      setSelected((prev) => {
                        const next = [...prev];
                        [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                        return next;
                      })
                    }
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`移除第${i + 1}条选择`}
                    onClick={() =>
                      setSelected((prev) => prev.filter((s) => s.recordId !== r.recordId))
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="lecture-record-save"
                type="button"
                disabled={exportBusy}
                onClick={() => void exportRecords()}
              >
                {exportBusy ? '正在准备…' : 'B版导出 / PDF'}
              </button>
            </div>
          ) : null}
          {state.records.length === 0 ? (
            <p>还没有已保存的记录。保存后可在这里选择并导出。</p>
          ) : (
            state.records.map((r) => (
              <div className="lecture-record-row" key={r.recordId}>
                <input
                  type="checkbox"
                  aria-label={`选择${r.title || `第${r.moveNumber}手`}`}
                  checked={selected.some((s) => s.recordId === r.recordId)}
                  disabled={
                    !selected.some((s) => s.recordId === r.recordId) && selected.length >= 50
                  }
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked
                        ? [...prev, { recordId: r.recordId, version: r.version }]
                        : prev.filter((s) => s.recordId !== r.recordId),
                    )
                  }
                />
                <button
                  type="button"
                  disabled={state.busy}
                  onClick={() =>
                    void store.open(r.recordId).then((ok) => {
                      if (ok) setView('EDITOR');
                    })
                  }
                >
                  <strong>{r.title || `第 ${r.moveNumber} 手`}</strong>
                  <small>
                    {r.sourceName} ·{' '}
                    {r.variationCount
                      ? `主线 ${r.baseMove} 手＋变化 ${r.variationCount} 手`
                      : `第 ${r.moveNumber} 手`}
                  </small>
                </button>
              </div>
            ))
          )}
          {state.nextCursor ? (
            <button type="button" disabled={state.busy} onClick={() => void store.loadMore()}>
              加载更多记录
            </button>
          ) : null}
        </div>
      )}
      {state.storageError ? (
        <p className="lecture-record-warning" role="alert">
          {state.storageError}
        </p>
      ) : null}
      {state.error || error ? (
        <p className="lecture-record-warning" role="alert">
          {state.error || error}
          {draft && state.error && !draft.operation ? (
            <button
              type="button"
              disabled={state.busy}
              onClick={() => {
                store.forkActive();
                setView('EDITOR');
              }}
            >
              将当前草稿另存为新记录
            </button>
          ) : null}
        </p>
      ) : null}
      <dialog
        ref={dialog}
        className={`lecture-record-dialog${exported ? ' lecture-record-dialog--print' : ''}`}
        aria-label={exported ? 'B版打印预览' : '记录棋图'}
        onCancel={() => {
          setDiagram(false);
          setExported(null);
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <header>
          <strong>
            {exported
              ? `B版打印预览 · ${pages || '…'}页`
              : draft
                ? lecturePositionLabel(draft.snapshot)
                : '记录棋图'}
          </strong>
          <div>
            {exported ? (
              <button
                type="button"
                disabled={!pages}
                onClick={() => {
                  frame.current?.contentWindow?.focus();
                  frame.current?.contentWindow?.print();
                }}
              >
                打印 / 另存为 PDF
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setDiagram(false);
                setExported(null);
              }}
            >
              关闭
            </button>
          </div>
        </header>
        {exported ? (
          <>
            <p>在打印窗口选择“另存为 PDF”，纸张 A4、横向，并关闭浏览器页眉页脚。</p>
            {error ? <p role="alert">{error}</p> : null}
            <iframe ref={frame} title="讲解记录打印页面" />
          </>
        ) : draft ? (
          <LectureDiagram snapshot={draft.snapshot} />
        ) : null}
      </dialog>
    </section>
  );
}
